import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { Timestamp } from "firebase/firestore";

import { db, functions } from "../FirebaseConfig";
import { DateField } from "../components/DateField";
import { LocationCombobox } from "../components/LocationCombobox";
import { WideSelect } from "../components/WideSelect";
import { ConflictModal, type ConflictDetails } from "../components/ConflictModal";
import { SavingOverlay } from "../components/SavingOverlay";
import { ClearableField } from "../components/ClearableField";
import { useAuth } from "../lib/auth-context";
import { useCollection } from "../lib/firestore-hooks";
import { useViewAsUser } from "../lib/view-as";
import { formatFecha, todaySantiago, tomorrowSantiago } from "../lib/format";
import { generateTimeGrid } from "../lib/time-grid";
import type {
  AeropuertoDoc,
  AvionDoc,
  BlackoutDoc,
  CuentaDoc,
  FlightPaxEntry,
  PasajeroDoc,
  UsuarioDoc,
  VueloDoc,
} from "../lib/types";

/**
 * /solicitar — flight request form. Port of v1's /solicitar with the
 * same fields and ordering, simplified by being a single-page React
 * component that calls the `submitFlight` HTTPS callable.
 *
 * The callable does the heavy lifting:
 *   - validates input + pax limits
 *   - looks up display names so the Vuelos doc is denormalized
 *   - assigns flight public_number atomically via a Firestore transaction
 *     on Counters/flight_{year}
 *   - creates the Vuelos doc in status='pending'
 */
export function Solicitar() {
  const { usuario } = useAuth();
  const [viewAsUser] = useViewAsUser();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnOf = params.get("return_of");
  const copyOf = params.get("copy_of");
  // Field-level prefill (used by the Claude hand-off: the assistant gathers
  // the flight details and links here with these params so the user reviews
  // and submits in the real form). Ignored when return_of/copy_of is present.
  const pfAircraft = params.get("af");
  const pfCuenta = params.get("cuenta");
  const pfFecha = params.get("fecha");
  const pfHora = params.get("hora");
  const pfOrigen = params.get("origen");
  const pfDestino = params.get("destino");
  const pfNumPax = params.get("numpax");
  const pfPiloto = params.get("piloto");
  const pfDisposicion = params.get("disposicion");
  const pfReposicion = params.get("reposicion");
  const pfPasajeros = params.get("pasajeros");
  const pfNotas = params.get("notas");

  const { rows: aircraft } = useCollection<AvionDoc>("Aviones", orderBy("displayName"));
  const { rows: accounts } = useCollection<CuentaDoc>("Cuentas", orderBy("code"));
  const { rows: aeropuertos } = useCollection<AeropuertoDoc>(
    "Aeropuertos",
    orderBy("displayCombined")
  );
  const { rows: usuarios } = useCollection<UsuarioDoc>("Usuarios", orderBy("displayName"));
  const { rows: pasajeros } = useCollection<PasajeroDoc>("Pasajeros", orderBy("fullName"));

  const pilotOptions = useMemo(
    () => (usuarios ?? []).filter((u) => u.roles?.includes("pilot")),
    [usuarios]
  );

  const aircraftOptions = useMemo(() => (aircraft ?? []).filter((a) => a.active), [aircraft]);
  // Filter by the user's allowed accountCodes when set. Admins (and
  // users with no restriction) see every active account.
  const accountOptions = useMemo(() => {
    const active = (accounts ?? []).filter((a) => a.active);
    const allowed = usuario?.accountCodes;
    const isAdminUser = !!usuario && (usuario.isAdmin || usuario.isSuperAdmin);
    if (isAdminUser || !allowed || allowed.length === 0) return active;
    const set = new Set(allowed);
    return active.filter((a) => set.has(a.code));
  }, [accounts, usuario]);
  const aeropuertoOptions = useMemo(
    () =>
      (aeropuertos ?? [])
        .filter((a) => a.active)
        .map((a) => ({
          icao: a.id,
          displayName: a.displayName,
          city: a.city ?? null,
        })),
    [aeropuertos]
  );

  // Form state.
  const [aircraftId, setAircraftId] = useState<string>("cirrus");
  const [accountCode, setAccountCode] = useState<string>("");
  const [fecha, setFecha] = useState<string>(() => tomorrowSantiago());
  const [hora, setHora] = useState<string>("");
  const [originIcao, setOriginIcao] = useState<string | null>(null);
  const [destinationIcao, setDestinationIcao] = useState<string | null>(null);
  const [numPax, setNumPax] = useState<number>(1);
  const [pilotUidRequested, setPilotUidRequested] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [pax, setPax] = useState<FlightPaxEntry[]>([]);
  // Post-flight disposition, set by the requester as part of the
  // request and confirmed (or changed) by the admin at approval.
  // Default "home" = the plane returns to base. Hidden when destination
  // already equals the base (the choice is moot).
  const [disposition, setDisposition] = useState<string>("home");
  const [postDestIcao, setPostDestIcao] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  // Two-step submit: clicking the button validates and opens a small
  // confirm panel with the flight summary; the user then confirms or
  // cancels. Reduces typo-submissions without an extra click for the
  // happy path (just an Enter / tap on the highlighted button).
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Idempotency key: one UUID per form mount. If the callable times
  // out client-side after the Firestore write has already committed,
  // the user retrying reuses the same token and the backend returns
  // the existing flight instead of creating a duplicate.
  const clientTokenRef = useRef<string>(
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
  // Surfaces the "Conflicto de horarios" modal when the backend
  // refuses the submission. The structured details power the
  // pre-populated message-to-admin.
  const [conflict, setConflict] = useState<ConflictDetails | null>(null);

  // Per-aircraft pax cap.
  const maxPax = aircraftOptions.find((a) => a.id === aircraftId)?.maxPax ?? 9;
  // Aircraft home base — drives whether the admin "Disposición"
  // dropdown needs to render. When the flight's destination already
  // equals the base, the post-flight ferry decision is moot ("vuelve
  // a base" = 0 distance, no ferry), so we hide the control.
  const homeBaseIcao =
    aircraftOptions.find((a) => a.id === aircraftId)?.homeBaseIcao ?? null;
  const destinationIsBase = !!homeBaseIcao && destinationIcao === homeBaseIcao;
  // If the destination is already the base, force-reset any prior
  // "relocate_other" selection so we don't accidentally submit a
  // ferry to a third airport.
  useEffect(() => {
    if (destinationIsBase && disposition !== "home") setDisposition("home");
  }, [destinationIsBase, disposition]);
  // Per-aircraft "common destinations" pinned at the top of the
  // origin/destination dropdowns. Admin curates this on
  // /admin/aircraft. Null/empty means "no pinned list" — combobox
  // falls back to alphabetical only.
  const selectedAircraftTopAirports =
    aircraftOptions.find((a) => a.id === aircraftId)?.topAirports ?? null;
  useEffect(() => {
    if (numPax > maxPax) setNumPax(maxPax);
  }, [maxPax, numPax]);
  // numPax (the dropdown selection) is the hard ceiling on named pax.
  // If the user reduces it below the count of chips already added,
  // trim the trailing entries so the form stays in sync.
  useEffect(() => {
    if (pax.length > numPax) setPax((prev) => prev.slice(0, numPax));
  }, [pax.length, numPax]);

  // Pre-fill defaults from user profile.
  useEffect(() => {
    if (!usuario) return;
    if (!accountCode && usuario.defaultAccountCode) {
      setAccountCode(usuario.defaultAccountCode);
    }
  }, [usuario, accountCode]);

  // When the user has only one allowed cuenta, the dropdown is hidden
  // (rendered as a static label) — auto-fill the value so the form
  // still validates.
  useEffect(() => {
    if (accountOptions.length === 1 && !accountCode) {
      setAccountCode(accountOptions[0]!.code);
    }
  }, [accountOptions, accountCode]);

  // If we landed here via ?return_of=ID or ?copy_of=ID, fetch the source
  // flight once and pre-fill the form with the same aircraft / account /
  // pilot / pax. For "return", swap origin↔destination so the user just
  // picks fecha + hora. Fecha is always left blank for copies.
  const [sourceLoaded, setSourceLoaded] = useState(false);
  useEffect(() => {
    if (sourceLoaded) return;
    const sourceId = returnOf ?? copyOf;
    if (!sourceId) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "Vuelos", sourceId));
        if (!snap.exists()) {
          setSourceLoaded(true);
          return;
        }
        const f = snap.data() as VueloDoc;
        setAircraftId(f.aircraftId);
        setAccountCode(f.accountCode ?? "");
        setNumPax(f.numPax);
        setPilotUidRequested(f.pilotUid ?? f.pilotUidRequested ?? "");
        setNotes(f.notes ?? "");
        setPax(f.pax ?? []);
        if (returnOf) {
          setOriginIcao(f.destinationIcao);
          setDestinationIcao(f.originIcao);
          // Same-day return is the common case; pre-fill the original
          // flight's fecha so the requester only needs to adjust hora.
          setFecha(f.fecha);
        } else {
          setOriginIcao(f.originIcao);
          setDestinationIcao(f.destinationIcao);
          setHora(f.hora);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[solicitar] source-flight prefill failed:", err);
      } finally {
        setSourceLoaded(true);
      }
    })();
  }, [returnOf, copyOf, sourceLoaded]);

  // Field-level prefill from query params (Claude hand-off). One-shot,
  // and only when not a return/copy. Waits for the passenger directory so
  // prefilled pax names map to existing Pasajeros docs (no duplicates).
  const [prefillDone, setPrefillDone] = useState(false);
  useEffect(() => {
    if (prefillDone || returnOf || copyOf) return;
    // Wait for the directory (pax mapping) and accounts (account validation).
    if (!pasajeros || !accounts) return;
    const hasAny =
      pfAircraft || pfCuenta || pfFecha || pfHora || pfOrigen ||
      pfDestino || pfNumPax || pfPiloto || pfDisposicion || pfReposicion ||
      pfPasajeros || pfNotas;
    if (!hasAny) {
      setPrefillDone(true);
      return;
    }
    if (pfAircraft) setAircraftId(pfAircraft);
    // Only apply a prefilled account the user is actually allowed to use;
    // otherwise leave it for the default-account logic / manual pick.
    if (pfCuenta) {
      const code = pfCuenta.toUpperCase();
      if (accountOptions.some((a) => a.code === code)) setAccountCode(code);
    }
    if (pfFecha && /^\d{4}-\d{2}-\d{2}$/.test(pfFecha)) setFecha(pfFecha);
    if (pfHora && /^\d{2}:\d{2}$/.test(pfHora)) setHora(pfHora);
    if (pfOrigen) setOriginIcao(pfOrigen.toUpperCase());
    if (pfDestino) setDestinationIcao(pfDestino.toUpperCase());
    // Match a prefilled pilot name to a real pilot option; ignore if no match.
    if (pfPiloto) {
      const want = pfPiloto.trim().toLowerCase();
      const match = pilotOptions.find(
        (p) => (p.displayName ?? "").toLowerCase() === want
      );
      if (match) setPilotUidRequested(match.id);
    }
    if (pfDisposicion && ["home", "stay", "relocate_other"].includes(pfDisposicion)) {
      setDisposition(pfDisposicion);
    }
    if (pfReposicion) setPostDestIcao(pfReposicion.toUpperCase());

    const dir = pasajeros.filter((p) => p.active);
    const names = pfPasajeros
      ? pfPasajeros.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 12)
      : [];
    const entries: FlightPaxEntry[] = names.map((name) => {
      const match = dir.find(
        (p) => p.fullName.toLowerCase() === name.toLowerCase()
      );
      return match
        ? {
            passengerId: match.id,
            fullName: match.fullName,
            family: match.family ?? null,
            rut: match.rut ?? null,
          }
        : { passengerId: null, fullName: name, family: null, rut: null };
    });

    let np: number | null = null;
    if (pfNumPax) {
      const n = parseInt(pfNumPax, 10);
      if (Number.isFinite(n) && n >= 0 && n <= 12) np = n;
    }
    if (np == null && names.length) np = names.length;
    if (np != null) setNumPax(Math.max(np, names.length));
    if (entries.length) setPax(entries);
    if (pfNotas) setNotes(pfNotas.slice(0, 500));
    setPrefillDone(true);
  }, [
    prefillDone, returnOf, copyOf, pasajeros, accounts, accountOptions, pilotOptions,
    pfAircraft, pfCuenta, pfFecha, pfHora, pfOrigen,
    pfDestino, pfNumPax, pfPiloto, pfDisposicion, pfReposicion, pfPasajeros, pfNotas,
  ]);

  // Watch blackouts for the chosen aircraft so we can warn before submit.
  const [blackouts, setBlackouts] = useState<BlackoutDoc[]>([]);
  useEffect(() => {
    if (!aircraftId) {
      setBlackouts([]);
      return;
    }
    const cutoff = Timestamp.fromDate(new Date(`${todaySantiago()}T00:00:00`));
    const q = query(
      collection(db, "Aviones", aircraftId, "Blackouts"),
      where("endAt", ">=", cutoff),
      orderBy("endAt", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: BlackoutDoc[] = [];
        snap.forEach((d) => list.push(d.data() as BlackoutDoc));
        setBlackouts(list);
      },
      () => setBlackouts([])
    );
    return () => unsub();
  }, [aircraftId]);

  const conflictingBlackout = useMemo(() => {
    if (!fecha || !hora || blackouts.length === 0) return null;
    const startMs = santiagoWallClockMs(fecha, hora);
    if (startMs == null) return null;
    return (
      blackouts.find(
        (b) =>
          b.startAt.toMillis() <= startMs && startMs < b.endAt.toMillis()
      ) ?? null
    );
  }, [fecha, hora, blackouts]);

  const isAdminUser = !!usuario && (usuario.isAdmin || usuario.isSuperAdmin);
  const adminAutoApprove = isAdminUser && !viewAsUser;

  async function createProvisionalAirport({
    icao,
    displayName,
    city,
  }: {
    icao: string;
    displayName: string;
    city: string | null;
  }): Promise<string> {
    const call = httpsCallable<
      { icao: string; displayName: string; city: string | null },
      { icao: string; existed: boolean }
    >(functions, "createProvisionalAirport");
    const r = await call({ icao, displayName, city });
    return r.data.icao;
  }

  function handleSubmit() {
    setError(null);
    if (!aircraftId) return setError("Selecciona avión.");
    if (!accountCode) return setError("Selecciona cuenta.");
    if (!fecha) return setError("Selecciona fecha.");
    if (!hora) return setError("Selecciona hora.");
    if (!originIcao) return setError("Selecciona origen.");
    if (!destinationIcao) return setError("Selecciona destino.");
    // Origen == destino is allowed: a local sightseeing flight that
    // returns to the same field. The backend assumes a 1-hour duration
    // when there's no distance record (which a same-airport pair never
    // has).
    if (adminAutoApprove && !pilotUidRequested) {
      return setError("Selecciona un piloto antes de aprobar.");
    }
    if (!destinationIsBase && disposition === "relocate_other" && !postDestIcao) {
      return setError("Indica el aeropuerto de reposicionamiento.");
    }
    setConfirming(true);
  }

  async function doSubmit() {
    setConfirming(false);
    setError(null);
    setSubmitting(true);
    try {
      const submitFlight = httpsCallable<
        Record<string, unknown>,
        { flightId: string; publicNumber: string }
      >(functions, "submitFlight");
      const r = await submitFlight({
        clientToken: clientTokenRef.current,
        aircraftId,
        accountCode,
        fecha,
        hora,
        originIcao,
        destinationIcao,
        numPax,
        notes: notes.trim() || null,
        pilotUidRequested: pilotUidRequested || null,
        pax,
        returnOfFlightId: returnOf,
        viewAsUser,
        // The requester now sets the post-flight disposition as part of
        // the request. It's stored on the pending doc and the admin
        // confirms (or changes) it at approval. When destination is the
        // base the choice is moot, so we force "home".
        disposition: destinationIsBase ? "home" : disposition,
        postFlightDestinationIcao:
          !destinationIsBase && disposition === "relocate_other"
            ? postDestIcao
            : undefined,
      });
      navigate(
        `/solicitar/gracias?id=${encodeURIComponent(r.data.publicNumber)}&uuid=${
          r.data.flightId
        }${returnOf ? "&from_return=1" : ""}${adminAutoApprove ? "&approved=1" : ""}`
      );
    } catch (err) {
      const e = err as {
        code?: string;
        message?: string;
        details?: ConflictDetails;
      };
      if (
        e.code === "functions/failed-precondition" &&
        e.details &&
        (e.details as ConflictDetails).kind === "flight-conflict"
      ) {
        setConflict(e.details as ConflictDetails);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
      setSubmitting(false);
    }
  }

  if (!aircraft || !accounts || !aeropuertos || !usuarios || !pasajeros) {
    return <p className="text-sm text-slate-500">Cargando opciones…</p>;
  }
  const pasajeroOptions = pasajeros.filter((p) => p.active);

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <SavingOverlay
        open={submitting}
        label={adminAutoApprove ? "Grabando vuelo…" : "Enviando solicitud…"}
      />
      <header>
        <div className="mb-2 h-[3px] w-8 rounded-full bg-brand-700" />
        <h1 className="text-2xl font-semibold tracking-tight text-brand-900">
          Solicitud
        </h1>
      </header>
      <Link
        to={adminAutoApprove ? "/admin/flights" : "/mis-vuelos"}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-brand-50"
      >
        <span aria-hidden>←</span> Cancelar / volver
      </Link>

      {returnOf ? (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-slate-800">
          Solicitando vuelo de retorno. Revisa fecha y hora antes de enviar.
        </div>
      ) : copyOf ? (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-slate-800">
          Copiando vuelo. Selecciona una fecha nueva.
        </div>
      ) : null}

      <Section title="Vuelo">
        <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-[7rem_minmax(0,28rem)] sm:gap-x-3 sm:gap-y-2 sm:items-center">
          <FieldRow label="Solicita">
            <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
              <span className="font-medium text-slate-900">
                {usuario?.displayName ?? "—"}
              </span>
              {usuario?.email ? (
                <span className="ml-2 text-slate-500">— {usuario.email}</span>
              ) : null}
            </p>
          </FieldRow>
          <FieldRow label="Cuenta" required>
            {accountOptions.length === 1 ? (
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {accountOptions[0]!.code} — {accountOptions[0]!.displayName}
              </p>
            ) : (
              <WideSelect
                value={accountCode}
                onChange={setAccountCode}
                options={accountOptions.map((a) => ({
                  value: a.code,
                  label: `${a.code} — ${a.displayName}`,
                }))}
                required
              />
            )}
          </FieldRow>
          <FieldRow label="Avión" required>
            <Select
              value={aircraftId}
              onChange={setAircraftId}
              options={aircraftOptions.map((a) => ({
                value: a.id,
                label: a.displayName,
              }))}
              required
            />
          </FieldRow>
          <FieldRow label="Fecha" required>
            <DateField
              value={fecha}
              onChange={setFecha}
              min={todaySantiago()}
              required
            />
          </FieldRow>
          <FieldRow label="Hora" required>
            <Select
              value={hora}
              onChange={setHora}
              options={generateTimeGrid().map((t) => ({ value: t, label: t }))}
              placeholder="Selecciona…"
              required
            />
          </FieldRow>
          <FieldRow label="Origen" required>
            <LocationCombobox
              value={originIcao}
              onChange={setOriginIcao}
              options={aeropuertoOptions}
              topAirports={selectedAircraftTopAirports}
              onCreate={createProvisionalAirport}
              required
            />
          </FieldRow>
          <FieldRow label="Destino" required>
            <LocationCombobox
              value={destinationIcao}
              onChange={setDestinationIcao}
              options={aeropuertoOptions}
              topAirports={selectedAircraftTopAirports}
              onCreate={createProvisionalAirport}
              required
            />
          </FieldRow>
          <FieldRow label="Cant. Pasajeros" required>
            <WideSelect
              value={String(numPax)}
              onChange={(v) => setNumPax(Number(v))}
              options={Array.from({ length: maxPax + 1 }, (_, i) => ({
                value: String(i),
                label: String(i),
              }))}
              required
            />
          </FieldRow>
          <FieldRow label="Piloto" required={adminAutoApprove}>
            <Select
              value={pilotUidRequested}
              onChange={setPilotUidRequested}
              options={[
                {
                  value: "",
                  label: adminAutoApprove ? "Selecciona piloto" : "Sin preferencia",
                },
                ...pilotOptions.map((p) => ({
                  value: p.id,
                  label: p.displayName ?? p.email ?? p.id,
                })),
              ]}
              required={adminAutoApprove}
            />
          </FieldRow>
          <FieldRow label="Pasajeros" alignTop>
            <PaxPicker
              value={pax}
              onChange={setPax}
              options={pasajeroOptions}
              maxPax={numPax}
            />
          </FieldRow>
          <FieldRow label="Notas" alignTop>
            <ClearableField value={notes} onClear={() => setNotes("")} textarea>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Cualquier información relevante para su vuelo, solicitud de catering, etc."
                className="block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </ClearableField>
          </FieldRow>
          {!destinationIsBase ? (
            <FieldRow
              label="Al terminar, avión:"
              alignTop
              hint="Qué hace el avión después de este vuelo. El admin lo confirma al aprobar."
            >
              <select
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              >
                <option value="home">
                  Vuelve a la base
                  {homeBaseIcao ? ` (${homeBaseIcao.replace(/^_/, "")})` : ""}
                </option>
                <option value="stay">Se queda en destino</option>
                <option value="relocate_other">Reposicionar a otro lugar</option>
              </select>
              <p className="mt-1.5 text-xs font-medium text-rose-700">
                {disposition === "home"
                  ? "⚠ Inmediatamente después de este vuelo se planificará un ferry de retorno a la base: un vuelo SIN pasajeros, solo para devolver el avión. NO es tu vuelo de regreso: si también vuelves tú, solicita la vuelta como otro vuelo."
                  : disposition === "stay"
                    ? "⚠ El avión queda en el destino después de este vuelo. No se planifica ferry de regreso; el retorno a base se coordinará con un vuelo posterior."
                    : "⚠ Inmediatamente después de este vuelo se planificará un ferry hacia el aeropuerto que indiques: un vuelo SIN pasajeros, solo para mover el avión."}
              </p>
            </FieldRow>
          ) : null}
          {!destinationIsBase && disposition === "relocate_other" ? (
            <FieldRow label="Reposicionar a">
              <input
                type="text"
                value={postDestIcao}
                onChange={(e) => setPostDestIcao(e.target.value.toUpperCase())}
                placeholder="SCEL"
                maxLength={4}
                className="block w-32 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm uppercase focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
              />
            </FieldRow>
          ) : null}
        </div>
      </Section>

      {conflictingBlackout ? (
        <div className="rounded border-2 border-orange-400 bg-orange-50 p-3 text-sm text-orange-900">
          <strong>Aviso:</strong> el avión está bloqueado el{" "}
          {formatFecha(fecha)} ·{" "}
          <span className="font-mono">
            {formatBlackoutRange(conflictingBlackout)}
          </span>
          {conflictingBlackout.reason ? ` · ${conflictingBlackout.reason}` : ""}.
          Considera elegir otra fecha o avión.
        </div>
      ) : null}

      {adminAutoApprove ? (
        <Section title="Aprobación automática">
          <p className="text-xs text-slate-500">
            Como es ADMIN, el vuelo queda aprobado de inmediato. La
            disposición del avión al terminar (más arriba) determina los
            ferries que se calculan.
          </p>
        </Section>
      ) : null}

      {error ? (
        <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={submitting}
          onClick={handleSubmit}
          className="rounded-md bg-brand-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-900 disabled:opacity-50"
        >
          {submitting
            ? (adminAutoApprove ? "Grabando…" : "Enviando…")
            : (adminAutoApprove ? "Grabar vuelo" : "Solicitar vuelo")}
        </button>
        <Link
          to={adminAutoApprove ? "/admin/flights" : "/mis-vuelos"}
          className="text-sm text-slate-500 hover:text-slate-700"
        >
          Cancelar
        </Link>
      </div>

      {confirming ? (
        <div
          className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4"
          onClick={() => setConfirming(false)}
        >
          <div
            className="my-8 w-full max-w-md rounded-lg bg-white p-6 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold tracking-tight text-brand-900">
              {adminAutoApprove ? "Confirmar grabación" : "Confirmar solicitud"}
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Revisa los datos antes de enviar.
            </p>
            <dl className="mt-3 space-y-1 text-sm text-slate-700">
              <div className="flex gap-2">
                <dt className="w-20 text-slate-500">Fecha</dt>
                <dd className="font-medium text-slate-900">
                  {formatFecha(fecha)} · {hora}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-20 text-slate-500">Avión</dt>
                <dd className="font-medium text-slate-900">
                  {aircraftOptions.find((a) => a.id === aircraftId)?.displayName ??
                    aircraftId}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-20 text-slate-500">Ruta</dt>
                <dd className="font-medium text-slate-900">
                  {aeropuertoOptions.find((a) => a.icao === originIcao)?.displayName ??
                    originIcao}{" "}
                  →{" "}
                  {aeropuertoOptions.find((a) => a.icao === destinationIcao)?.displayName ??
                    destinationIcao}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-20 text-slate-500">Pax</dt>
                <dd className="font-medium text-slate-900">{numPax}</dd>
              </div>
              {pilotUidRequested ? (
                <div className="flex gap-2">
                  <dt className="w-20 text-slate-500">Piloto</dt>
                  <dd className="font-medium text-slate-900">
                    {pilotOptions.find((p) => p.id === pilotUidRequested)
                      ?.displayName ?? pilotUidRequested}
                  </dd>
                </div>
              ) : null}
            </dl>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-brand-50"
              >
                Ajustar
              </button>
              <button
                type="button"
                autoFocus
                onClick={doSubmit}
                className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-900"
              >
                {adminAutoApprove ? "Grabar vuelo" : "Enviar solicitud"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {conflict ? (
        <ConflictModal
          conflict={conflict}
          attempt={{
            fecha: fecha ? formatFecha(fecha) : "",
            hora,
            aircraftDisplayName:
              aircraftOptions.find((a) => a.id === aircraftId)?.displayName ?? aircraftId,
            origin:
              aeropuertoOptions.find((a) => a.icao === originIcao)?.displayName ??
              (originIcao ?? ""),
            destination:
              aeropuertoOptions.find((a) => a.icao === destinationIcao)?.displayName ??
              (destinationIcao ?? ""),
          }}
          context="solicitar"
          onClose={() => setConflict(null)}
        />
      ) : null}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-5">
      <h2 className="text-sm font-medium uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * One row of the request datasheet, mirroring the admin Aprobar panel:
 * a small uppercase label on the left, the input control on the right.
 * Renders the label/input as siblings of the parent grid via
 * `display: contents` on sm+ so labels and inputs across all rows
 * share the same column tracks. On mobile the wrapper collapses to a
 * plain block so each row stacks vertically.
 */
function FieldRow({
  label,
  required,
  hint,
  alignTop,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: React.ReactNode;
  alignTop?: boolean;
  children: React.ReactNode;
}) {
  const labelAlign = alignTop ? " sm:self-start sm:pt-2" : "";
  return (
    <div className="block sm:contents">
      <div
        className={
          "text-sm font-medium text-slate-700 sm:text-[11px] sm:uppercase sm:tracking-wider sm:font-semibold sm:text-slate-500 sm:text-right"
          + labelAlign
        }
      >
        {label}
        {required ? <span className="text-rose-600"> *</span> : null}
      </div>
      <div>
        {children}
        {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
      </div>
    </div>
  );
}

/** Santiago wall-clock YYYY-MM-DD + HH:MM -> UTC ms; null on bad input. */
function santiagoWallClockMs(fecha: string, hora: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha) || !/^\d{2}:\d{2}$/.test(hora)) {
    return null;
  }
  const [y, m, d] = fecha.split("-").map(Number);
  const [hh, mm] = hora.split(":").map(Number);
  const wallUtc = Date.UTC(y!, m! - 1, d!, hh!, mm!, 0);
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Santiago",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(new Date(wallUtc));
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const observed = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
  return wallUtc - (observed - wallUtc);
}

/** "DD/MMM/YYYY → DD/MMM/YYYY" — for the warning banner. */
function formatBlackoutRange(b: BlackoutDoc): string {
  const fmtDate = (t: Timestamp) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Santiago",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(t.toDate());
  return `${formatFecha(fmtDate(b.startAt))} → ${formatFecha(fmtDate(b.endAt))}`;
}

interface PasajeroRow extends PasajeroDoc {
  id: string;
}

/**
 * Combo of a chip list of selected pax + a typeahead input that lets
 * the requester pick from the Pasajeros directory or add an ad-hoc
 * name. The chosen pax array is what gets stored on the Vuelos doc
 * (denormalized: passengerId | null + fullName + family + rut).
 *
 * Hard-capped by the aircraft's pax limit so the form can't oversell
 * the plane.
 */
export function PaxPicker({
  value,
  onChange,
  options,
  maxPax,
}: {
  value: FlightPaxEntry[];
  onChange: (next: FlightPaxEntry[]) => void;
  options: PasajeroRow[];
  maxPax: number;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const atLimit = value.length >= maxPax;

  const term = query.trim().toLowerCase();
  const usedIds = new Set(
    value.map((p) => p.passengerId).filter(Boolean) as string[]
  );
  const matches = !term
    ? options.filter((p) => !usedIds.has(p.id)).slice(0, 8)
    : options
        .filter((p) => {
          if (usedIds.has(p.id)) return false;
          const hay = `${p.fullName} ${p.family ?? ""} ${p.rut ?? ""}`.toLowerCase();
          return hay.includes(term);
        })
        .slice(0, 8);

  function addFromDirectory(p: PasajeroRow) {
    if (atLimit) return;
    onChange([
      ...value,
      {
        passengerId: p.id,
        fullName: p.fullName,
        family: p.family ?? null,
        rut: p.rut ?? null,
      },
    ]);
    setQuery("");
    setOpen(false);
  }
  function addAdHoc() {
    if (atLimit) return;
    const name = query.trim();
    if (!name) return;
    onChange([
      ...value,
      { passengerId: null, fullName: name, family: null, rut: null },
    ]);
    setQuery("");
    setOpen(false);
  }
  function removeAt(i: number) {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  }

  return (
    <div className="mt-2 space-y-2">
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((p, i) => (
            <li
              key={`${p.passengerId ?? "adhoc"}-${i}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-800"
            >
              <span>{p.fullName}</span>
              {p.family ? (
                <span className="text-slate-500">{p.family}</span>
              ) : null}
              {p.passengerId == null ? (
                <span
                  className="rounded bg-amber-200 px-1 text-[10px] font-medium uppercase tracking-wide text-amber-900"
                  title="Se agregará al directorio al enviar"
                >
                  nuevo
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="ml-0.5 text-slate-500 hover:text-rose-700"
                aria-label="Quitar"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {atLimit ? (
        <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Se ingresaron los {maxPax} pasajeros del vuelo. Para agregar
          más, aumenta el número de pasajeros arriba.
        </p>
      ) : (
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (matches.length > 0) addFromDirectory(matches[0]!);
                else if (query.trim()) addAdHoc();
              } else if (e.key === "Escape") setOpen(false);
            }}
            placeholder="Buscar en directorio o tipear un nombre nuevo…"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 pr-9 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-50"
          />
          {query ? (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                setQuery("");
              }}
              aria-label="Borrar"
              className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <svg
                viewBox="0 0 20 20"
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              >
                <path d="M5 5l10 10M15 5L5 15" />
              </svg>
            </button>
          ) : null}
          {open ? (
            <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
              {matches.map((p) => (
                <li
                  key={p.id}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addFromDirectory(p);
                  }}
                  className="cursor-pointer px-3 py-1.5 text-sm text-slate-800 hover:bg-sky-100"
                >
                  <span>{p.fullName}</span>
                  {p.family ? (
                    <span className="ml-2 text-xs text-slate-500">{p.family}</span>
                  ) : null}
                  {p.rut ? (
                    <span className="ml-2 font-mono text-xs text-slate-500">
                      {p.rut}
                    </span>
                  ) : null}
                </li>
              ))}
              {query.trim() ? (
                <li
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addAdHoc();
                  }}
                  className="cursor-pointer border-t border-slate-100 px-3 py-1.5 text-sm text-sky-700 hover:bg-sky-100"
                >
                  + Agregar “{query.trim()}” como pasajero nuevo
                </li>
              ) : matches.length === 0 ? (
                <li className="px-3 py-1.5 text-xs text-slate-500">
                  Tipea un nombre para buscar o agregar.
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <WideSelect
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      required={required}
    />
  );
}
